import { defaultSleep, fetchPool, NvdError, type RollingLimiter } from "./fetch.js";
import {
  isCurrent,
  isUsable,
  makeRecord,
  mergeEntries,
  readPool,
  type CvePoolRecord,
  type PoolSnapshot,
  type PoolSource,
  type PoolWriter,
} from "./cache.js";
import type { CveEntry } from "./normalize.js";
import { EMBEDDED_SEEDS } from "./seeds.js";
import { isoUtcDate, poolWindowFor, startOfUtcDay } from "../schedule/clock.js";

/** Days of publications in the pool. */
export const POOL_DAYS = 7;

/**
 * How long a cold start waits for NVD's first page before serving the seeds.
 *
 * Long enough that a fast feed still supplies the first disclosure, short
 * enough that a slow one cannot hold narration back: measured first pages have
 * taken 9-50 s.
 */
export const FIRST_PAGE_GRACE_MS = 400;
const DAY_MS = 86_400_000;
/**
 * How long a station waits before trying again to fill an incomplete pool, and
 * how long after UTC midnight it fetches the new day once the pool is complete.
 */
export const RETRY_INTERVAL_MS = 5 * 60_000;

export interface PoolResolution {
  entries: CveEntry[];
  source: PoolSource;
  windowStartUtc: string;
  windowEndUtc: string;
  refresh: boolean;
  /**
   * The in-flight fill of the window, when one was started. Exposed so the
   * caller can await or abort it during teardown instead of leaving a stray
   * fetch writing to a closed cache writer. It never rejects.
   */
  refreshTask?: Promise<void>;
}

export interface ResolveOptions {
  writer: PoolWriter;
  now: () => number;
  signal: AbortSignal;
  fetchImpl?: typeof fetch;
  onStatus?: (message: string) => void;
  /** Shared across the page; NVD's anonymous quota is per IP, not per call. */
  limiter?: RollingLimiter;
  /**
   * Called with the whole merged pool, in slot order, every time a fetched page
   * is persisted. A running station uses this to pick from the larger pool for
   * slots it has not prepared yet.
   */
  onPool?: (entries: CveEntry[]) => void;
}

export interface MaintainOptions extends ResolveOptions {
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
  retryIntervalMs?: number;
}

/** The window's UTC day keys, newest first: the order they are fetched in. */
export function poolDays(nowMs: number, days: number = POOL_DAYS): string[] {
  const window = poolWindowFor(nowMs, days);
  const keys: string[] = [];
  for (let ms = window.endMs - DAY_MS; ms >= window.startMs; ms -= DAY_MS) keys.push(isoUtcDate(ms));
  return keys;
}

/**
 * Resolve the pool for the current window.
 *
 * Order: every day stored and complete -> serve, no fetch. Some usable days ->
 * serve them now, fill the rest in the background. Nothing cached -> wait for
 * the first page of the newest day, then fill the rest in the background. If
 * not even one page arrives, the embedded seeds (never dead air), and
 * `maintainPool` keeps trying.
 */
export async function resolvePool(options: ResolveOptions): Promise<PoolResolution> {
  const nowMs = options.now();
  const window = poolWindowFor(nowMs, POOL_DAYS);
  const days = poolDays(nowMs);
  const span = { windowStartUtc: window.startUtc, windowEndUtc: window.endUtc };

  const cached = await readPool(options.writer, days);
  if (cached?.complete) {
    return { ...span, entries: sorted(cached.entries), source: "cache-exact", refresh: false };
  }

  if (cached) {
    const refreshTask = fillWindow(options, days, false);
    return { ...span, entries: sorted(cached.entries), source: "cache-stale", refresh: true, refreshTask };
  }

  options.onStatus?.("Fetching the latest disclosures.");
  // Nothing cached. Wait a beat for the first page, because a feed that answers
  // quickly should still be read; if it does not, serve the embedded seeds and
  // let the fill upgrade the pool while the station is already on air. Waiting
  // a whole NVD page here puts a 30-50 s download in front of the first
  // disclosure, which is longer than the entire model load on a warm visit.
  let deliverFirst: ((entries: CveEntry[]) => void) | null = null;
  const firstPage = new Promise<CveEntry[] | null>((resolve) => {
    deliverFirst = (entries) => resolve(entries);
  });
  let graceTimer: ReturnType<typeof setTimeout> | undefined;
  const grace = new Promise<CveEntry[] | null>((resolve) => {
    graceTimer = setTimeout(() => resolve(null), FIRST_PAGE_GRACE_MS);
  });
  const refreshTask = fillWindow(options, days, true, (entries) => deliverFirst?.(entries));
  const early = await Promise.race([firstPage, grace]);
  if (graceTimer !== undefined) clearTimeout(graceTimer);
  if (early) {
    return { ...span, entries: early, source: "network", refresh: true, refreshTask };
  }
  return { ...span, entries: sorted(EMBEDDED_SEEDS), source: "seeds", refresh: true, refreshTask };
}

/**
 * Keep the pool filling for as long as the station runs.
 *
 * An incomplete pool is retried every `retryIntervalMs`; this is what lets a
 * station that started on the seeds upgrade itself once NVD answers. A complete
 * pool waits for the next UTC day, when the window gains a day. Ends when the
 * signal aborts; never rejects.
 */
export async function maintainPool(options: MaintainOptions): Promise<void> {
  const sleep = options.sleep ?? defaultSleep;
  const retryMs = options.retryIntervalMs ?? RETRY_INTERVAL_MS;
  try {
    while (!options.signal.aborted) {
      const nowMs = options.now();
      const snapshot = await readPool(options.writer, poolDays(nowMs));
      const waitMs = snapshot?.complete ? startOfUtcDay(nowMs) + DAY_MS - nowMs + retryMs : retryMs;
      await sleep(waitMs, options.signal);
      await fillWindow(options, poolDays(options.now()), false);
    }
  } catch {
    // Aborted while sleeping: the station is being torn down.
  }
}

/**
 * One pass over the window, newest day first, resuming each incomplete day from
 * its stored cursor. Every page is persisted before the next is requested, so a
 * transfer that dies partway keeps everything before it. The pass stops at the
 * first NVD failure: the next day would almost certainly fail the same way, and
 * the quota is better spent on the next retry. Never rejects.
 */
async function fillWindow(
  options: ResolveOptions,
  days: string[],
  announce: boolean,
  onFirstPage?: (entries: CveEntry[]) => void,
): Promise<void> {
  let delivered = false;
  const publish = async () => {
    const snapshot = await readPool(options.writer, days);
    if (!snapshot || snapshot.entries.length === 0) return;
    const entries = sorted(snapshot.entries);
    delivered = true;
    onFirstPage?.(entries);
    options.onPool?.(entries);
  };

  try {
    for (const day of days) {
      if (options.signal.aborted) return;
      await fillDay(options, day, publish);
    }
  } catch (error) {
    if (announce && !delivered && error instanceof NvdError) {
      options.onStatus?.(`NVD unavailable (${error.message}); using the fallback pool.`);
    }
  }

  if (!options.signal.aborted) await prune(options.writer, days);
}

async function fillDay(options: ResolveOptions, day: string, publish: () => Promise<void>): Promise<void> {
  const stored = await options.writer.read(day).catch(() => undefined);
  if (isCurrent(stored) && stored.complete) return;

  let record: CvePoolRecord = isCurrent(stored)
    ? stored
    : makeRecord({
        windowStartUtc: day,
        windowEndUtc: nextDay(day),
        entries: [],
        complete: false,
        nextStartIndex: 0,
        totalResults: 0,
        fetchedAt: options.now(),
      });

  await fetchPool({
    windowStartUtc: record.windowStartUtc,
    windowEndUtc: record.windowEndUtc,
    startIndex: record.nextStartIndex,
    signal: options.signal,
    now: options.now,
    fetchImpl: options.fetchImpl,
    // Without this, every call builds its own limiter and the per-page quota
    // guarantee evaporates: a background fill plus a fresh station could
    // dispatch five requests each inside the same thirty-second window.
    limiter: options.limiter,
    onPage: async (page) => {
      record = makeRecord({
        windowStartUtc: record.windowStartUtc,
        windowEndUtc: record.windowEndUtc,
        // Resuming after NVD's totals shift can repeat a record; IDs dedupe it.
        entries: mergeEntries([record, page]),
        complete: page.done,
        nextStartIndex: page.nextStartIndex,
        totalResults: page.totalResults,
        fetchedAt: options.now(),
      });
      await options.writer.write(record);
      await publish();
    },
  });
}

/**
 * Drop days that have left the window, but only once the window has something
 * to serve: an old cache is still better than the seeds.
 */
async function prune(writer: PoolWriter, days: string[]): Promise<void> {
  const wanted = new Set(days);
  const records = await writer.list().catch(() => [] as CvePoolRecord[]);
  if (!records.some((record) => wanted.has(record.windowStartUtc) && isUsable(record))) return;
  for (const record of records) {
    if (!wanted.has(record.windowStartUtc)) await writer.delete(record.windowStartUtc).catch(() => undefined);
  }
}

function nextDay(day: string): string {
  return isoUtcDate(Date.parse(`${day}T00:00:00.000Z`) + DAY_MS);
}

/** Pool order is fixed by CVE ID, so slot selection agrees across clients. */
export function sorted(entries: CveEntry[]): CveEntry[] {
  return [...entries].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

export type { CveEntry, PoolSnapshot };
